'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import Table from '@/shared/Table';
import Modal from '@/shared/Modal';
import SelectDropdown from '@/shared/SelectDropdown';
import TemplateChecklist from '@/components/ui/CheckList';
import { formatDateToCustomFormat } from '@/helpers/dateFormatter';
import {
  useAddDocumentMutation,
  useDownloadSampleDocumentsMutation,
  useGetDocumentTypesQuery,
  useGetSampleDocumentsQuery,
  useDeleteDocumentMutation,
  useUpdateDocumentMutation,
  useFetchMandateStatusQuery,
} from '@/redux/services/brokerService';
import { showToast } from '@/shared/Toast';
import { downloadFile, handleSampleDocumentsDownload } from '@/utils/downloadUtils';
import { useAddPropertyDocumentMutation, useDeletePropertyDocumentMutation, useUpdatePropertyDocumentMutation } from '@/redux/services/propertyService';

// eslint-disable-next-line sonarjs/cognitive-complexity
const DocumentsList = (props) => {
  const prospectId = localStorage.getItem('currentProspectId');
  const propertyId = localStorage.getItem('currentPropertyId') || props.documentPropertyById;
  // UI state
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [isSamplesModalOpen, setIsSamplesModalOpen] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [isTypeLocked, setIsTypeLocked] = useState(false);
  const [search, setSearch] = useState('');
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [shouldFetchMandate, setShouldFetchMandate] = useState(false);
  const [mandateHas404, setMandateHas404] = useState(false);

  const { isFetching: isMandateStatusFetching, refetch, error: mandateError } = useFetchMandateStatusQuery(propertyId ? { propertyId } : undefined, { skip: !propertyId });
  // API queries
  const { data: documentTypesData, isLoading: isDocumentTypesLoading } = useGetDocumentTypesQuery(props.type);
  const { data: sampleDocumentsData } = useGetSampleDocumentsQuery(props.sampleType || props.type);

  // Mutations
  const [addDocument] = useAddDocumentMutation();
  const [downloadSampleDocuments] = useDownloadSampleDocumentsMutation();
  const [updateDocument] = useUpdateDocumentMutation();
  const [updatePropertyDocument] = useUpdatePropertyDocumentMutation();
  const [addPropertyDocument] = useAddPropertyDocumentMutation();
  const [deleteDocument] = useDeleteDocumentMutation();
  const [deletePropertyDocument] = useDeletePropertyDocumentMutation();

  // Form & table data
  const [form, setForm] = useState({
    typeId: '',
    documentType: '',
    remarks: '',
    file: null,
  });
  const [originalData, setOriginalData] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, _setError] = useState(null);
  const [searchTimeout, setSearchTimeout] = useState(null);

  const [checkedItems, setCheckedItems] = useState({});

  useEffect(() => {
    if (!props.documents) return;
    const content = props.documents?.data?.content || props.documents?.content || [];

    const baseRows = Array.isArray(content)
      ? content?.map((doc) => ({
          id: doc.documentId,
          typeId: doc.typeId || doc.documentTypeId || doc.type_id,
          type: doc.documentType,
          remarks: doc.remarks || '-',
          uploadedBy: doc.uploadedBy || '-',
          uploadedOn: doc.uploadedOn,
          formattedUploadedOn: doc.uploadedOn ? formatDateToCustomFormat(doc.uploadedOn) : '-',
          fileUrl: doc.fileUrl || null,
          status: doc.status,
          isPlaceholder: !!doc.isPlaceholder,
          isMandate: (doc.documentType || '').toString().toLowerCase().includes('mandate'),
        }))
      : [];

    setOriginalData(baseRows);
    setData(baseRows);

    if (props.onDocumentsChange) {
      const hasUploaded = baseRows.some((row) => {
        const status = (row.status || '').toString().toUpperCase();
        return status === 'UPLOADED' && !!row.fileUrl;
      });
      props.onDocumentsChange(hasUploaded);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.documents, props.onDocumentsChange]);

  // Handle mandate status fetch error (404)
  useEffect(() => {
    if (mandateError?.status === 404) {
      setMandateHas404(true);
    }
  }, [mandateError]);

  // Handle search filtering
  useEffect(() => {
    const normalized = search ? search.toLowerCase() : '';

    const filtered = normalized
      ? originalData.filter((doc) => {
          const type = doc.type ? doc.type.toLowerCase() : '';
          const remarks = doc.remarks ? doc.remarks.toLowerCase() : '';
          const uploadedBy = doc.uploadedBy ? doc.uploadedBy.toLowerCase() : '';
          const status = doc.status ? doc.status.toString().toLowerCase() : '';

          return type.includes(normalized) || remarks.includes(normalized) || uploadedBy.includes(normalized) || status.includes(normalized);
        })
      : [...originalData];

    setData(filtered);
  }, [search, originalData]);

  // Compute filtered dropdown options (no duplicates)
  const filteredDocumentTypeOptions = useMemo(
    () => {
      const uploadedTypes = originalData
        .filter((doc) => {
          const status = (doc.status || '').toString().toUpperCase();
          return status === 'UPLOADED' && doc.id !== editingId;
        })
        .map((doc) => doc.type?.toLowerCase());

      return (documentTypesData?.data || [])
        .filter((type) => {
          if (form.typeId && form.typeId === type.typeId) {
            return true;
          }

          if (editingId) {
            const currentDoc = originalData.find((d) => d.id === editingId);
            if (currentDoc?.type?.toLowerCase() === type.documentType.toLowerCase()) {
              return true;
            }
          }
          if (type.isDefault) {
            return false;
          }

          return !uploadedTypes.includes(type.documentType.toLowerCase());
        })
        .map((type) => ({
          value: type.typeId,
          label: type.documentType,
        }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [documentTypesData, originalData, editingId, props.type, form.typeId]
  );

  // Checkbox logic
  const handleCheckboxChange = (id) => {
    setCheckedItems((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const openEditModal = (document) => {
    if (!document) {
      return;
    }

    if (!documentTypesData?.data) {
      showToast('error', 'Document types not loaded yet');
      return;
    }

    // Safely get document properties with fallbacks
    const documentType = document.type || '';
    const documentId = document.id;

    if (!documentId) {
      showToast('error', 'Invalid document data');
      return;
    }

    // Find the document type in the documentTypesData
    const docType = documentTypesData.data.find((type) => type.documentType?.toLowerCase() === documentType.toLowerCase());

    if (!docType) {
      showToast('error', 'Could not find document type');
      return;
    }

    setEditingId(documentId);
    setIsTypeLocked(true);
    setForm({
      typeId: docType.typeId,
      documentType: docType.documentType,
      remarks: document.remarks || '',
      file: null,
    });
    setIsAddOpen(true);
  };
  const hasSelectedItems = useMemo(() => Object.values(checkedItems).some(Boolean), [checkedItems]);

  // File upload handler
  const handleSubmit = async (formData) => {
    try {
      setLoading(true);
      const documentData = new FormData();
      documentData.append('typeId', formData.typeId);
      documentData.append('remarks', formData.remarks || '');
      documentData.append('documentName', formData.documentType);
      if (formData.file) documentData.append('file', formData.file);
      let response;
      if (editingId) {
        if (props.type === 'BROKER') {
          response = await updateDocument({
            brokerId: prospectId || props.brokerId,
            documentId: editingId,
            documentData,
          }).unwrap();
        } else {
          response = await updatePropertyDocument({
            propertyId,
            documentId: editingId,
            documentData,
          }).unwrap();
        }
      } else {
        if (props.type === 'BROKER') {
          response = await addDocument({
            brokerId: prospectId || props.brokerId,
            documentData,
          }).unwrap();
        } else {
          response = await addPropertyDocument({
            propertyId,
            documentData,
          }).unwrap();
        }
      }

      showToast('success', response?.message || `Document ${editingId ? 'updated' : 'uploaded'} successfully`);
      setForm({ typeId: '', documentType: '', remarks: '', file: null });
      setIsAddOpen(false);
      setEditingId(null);

      if (props.refetchDocuments) await props.refetchDocuments();
      if (props.onDocumentsChange) props.onDocumentsChange(true);
    } catch (err) {
      showToast('error', err?.data?.message || 'Failed to save document');
    } finally {
      setLoading(false);
    }
  };

  // File validation
  const validateFile = (file) => {
    const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    const allowedExtensions = ['.pdf', '.jpg', '.jpeg', '.png'];

    // Check file size
    if (file && file.size > maxSize) {
      showToast('error', 'File size must be less than 5 MB');
      return false;
    }

    // Check file type
    if (file && !allowedTypes.includes(file.type)) {
      showToast('error', 'Only PDF, JPG, JPEG, PNG files are allowed');
      return false;
    }

    // Additional check using file extension
    if (file) {
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedExtensions.includes(fileExtension)) {
        showToast('error', 'Only PDF, JPG, JPEG, PNG files are allowed');
        return false;
      }
    }

    return true;
  };

  const handleFileSelect = (file) => {
    if (validateFile(file)) {
      setForm((f) => ({ ...f, file }));
    }
  };

  // Drag and drop
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };
  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };
  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files[0]) {
      handleFileSelect(files[0]);
    }
  };

  // Delete handlers
  const handleDelete = (document) => {
    setDocumentToDelete(document);
    setIsDeleteOpen(true);
  };

  const confirmDelete = async () => {
    if (!documentToDelete) return;
    setIsUpdating(true);
    try {
      let response;

      if (props.type === 'BROKER') {
        response = await deleteDocument({
          brokerId: prospectId || props.brokerId,
          documentType: documentToDelete.type,
        }).unwrap();
      } else if (props.type === 'PROPERTY') {
        response = await deletePropertyDocument({
          propertyId,
          documentId: documentToDelete.id,
        }).unwrap();
      } else {
        throw new Error('Invalid document type for deletion');
      }

      const updatedList = originalData.filter((item) => item.id !== documentToDelete.id);
      setOriginalData(updatedList);
      setData(updatedList);

      if (props.onDocumentsChange) props.onDocumentsChange(updatedList.length > 0);
      if (updatedList.length === 0) {
        setSelectedDocument(null);
        if (props.onSelectDocument) props.onSelectDocument(null);
      }

      if (props.refetchDocuments) await props.refetchDocuments();
      showToast('success', response?.message || 'Document deleted successfully');
    } catch (error) {
      showToast('error', error?.data?.message || 'Failed to delete document');
    } finally {
      setIsUpdating(false);
      setDocumentToDelete(null);
      setIsDeleteOpen(false);
    }
  };
  const cancelDelete = () => {
    setDocumentToDelete(null);
    setIsDeleteOpen(false);
  };

  const handleCloseModal = () => {
    setForm({ typeId: '', type: '', remarks: '', file: null });
    setEditingId(null);
    setIsTypeLocked(false);
    setIsAddOpen(false);
  };

  const handleDownloadSelected = async () => {
    const selectedDocs = sampleDocumentsData?.data?.filter((doc) => checkedItems[doc.id]).map((doc) => doc.name) || [];
    setIsDownloading(true);
    try {
      const result = await handleSampleDocumentsDownload(selectedDocs, downloadSampleDocuments);
      showToast('success', result.message);
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setIsDownloading(false);
      setIsSamplesModalOpen(false);
    }
  };

  const handleRowClick = (row) => {
    setSelectedDocument(row);
    if (props.onSelectDocument) props.onSelectDocument(row);
    if (props.onMandateStatus) props.onMandateStatus(mandateHas404);
  };

  const tableColumns = useMemo(
    () => [
      { key: 'type', label: 'Type' },
      { key: 'remarks', label: 'Remarks' },
      {
        key: 'status',
        label: 'Status',
        render: (row) => {
          const raw = (row.status || '').toString();
          if (!raw) return '-';
          const upper = raw.toUpperCase();
          if (upper === 'UPLOADED') return 'Uploaded';
          if (upper === 'PENDING') return 'Pending';
          return raw;
        },
      },
      { key: 'uploadedBy', label: 'Uploaded By' },
      {
        key: 'formattedUploadedOn',
        label: 'Uploaded On',
        render: (row) => row.formattedUploadedOn || (row.uploadedOn ? formatDateToCustomFormat(row.uploadedOn) : '-') || '-',
        className: 'whitespace-nowrap',
      },
    ],
    []
  );

  const tableActions = (row) => {
    const isMandate = (row.type || '').toLowerCase() === 'mandate';
    const status = (row.status || '').toString().toUpperCase();
    const isPending = status === 'PENDING';
    const isUploaded = status === 'UPLOADED';
    return (
      <div className="flex items-center space-x-3 text-gray-500">
        {(() => {
          // Mandate logic
          if (isMandate) {
            if (isPending) {
              // Mandate + Pending: refresh enabled, download disabled
              return (
                <>
                  <button
                    type="button"
                    className="text-[#225874] cursor-pointer border-0 bg-transparent p-0"
                    disabled={isMandateStatusFetching}
                    onClick={async (e) => {
                      e.stopPropagation();
                      setShouldFetchMandate(true);
                      const result = await refetch();
                      if (result?.data) {
                        showToast('success', result.data.data || 'Mandate status refreshed');
                        if (props.refetchDocuments) {
                          props.refetchDocuments();
                        }
                      } else if (result?.error) {
                        showToast('error', result.error?.data?.message || 'Mandate document not found');
                      }
                      setShouldFetchMandate(false);
                    }}
                  >
                    <span className="material-symbols-outlined cursor-pointer icon mr-1">{isMandateStatusFetching ? 'autorenew' : 'refresh'}</span>
                  </button>
                  <button type="button" className="text-[#4D8BFF] opacity-40 cursor-not-allowed" disabled>
                    <span className="material-symbols-outlined cursor-not-allowed icon mr-1">download</span>
                  </button>
                  <button type="button" className="text-red-500 opacity-40 cursor-not-allowed" disabled>
                    <span className="material-symbols-outlined cursor-not-allowed icon mr-1">delete</span>
                  </button>
                </>
              );
            } else if (isUploaded) {
              // Mandate + Uploaded: refresh disabled, download enabled
              return (
                <>
                  <button type="button" className="text-[#225874] opacity-60 cursor-not-allowed" disabled>
                    <span className="material-symbols-outlined cursor-not-allowed icon mr-1">refresh</span>
                  </button>
                  <button
                    type="button"
                    className="text-[#4D8BFF]"
                    onClick={async (e) => {
                      e.stopPropagation();
                      if (row.fileUrl) {
                        try {
                          await downloadFile(row.fileUrl, row.type || 'document');
                        } catch (error) {
                          showToast('error', error.message || 'Failed to download file');
                        }
                      } else {
                        showToast('error', 'No file available to download.');
                      }
                    }}
                  >
                    <span className="material-symbols-outlined cursor-pointer icon mr-1">download</span>
                  </button>
                  <button type="button" className="text-red-500 opacity-40 cursor-not-allowed" disabled>
                    <span className="material-symbols-outlined cursor-not-allowed icon mr-1">delete</span>
                  </button>
                </>
              );
            }
            // Mandate + other status: all disabled
            return (
              <>
                <button type="button" className="text-[#225874] opacity-60 cursor-not-allowed" disabled>
                  <span className="material-symbols-outlined cursor-not-allowed icon mr-1">refresh</span>
                </button>
                <button type="button" className="text-[#4D8BFF] opacity-40 cursor-not-allowed" disabled>
                  <span className="material-symbols-outlined cursor-not-allowed icon mr-1">download</span>
                </button>
                <button type="button" className="text-red-500 opacity-40 cursor-not-allowed" disabled>
                  <span className="material-symbols-outlined cursor-not-allowed icon mr-1">delete</span>
                </button>
              </>
            );
          }

          // For Pending rows, show Upload action; disable Download/Delete
          if (isPending) {
            return (
              <>
                <button
                  type="button"
                  className="text-[#225874]"
                  onClick={(e) => {
                    e.stopPropagation();

                    // Preselect document type for upload
                    let typeMeta = null;
                    if (Array.isArray(documentTypesData?.data)) {
                      typeMeta = documentTypesData.data.find((t) => t.typeId === row.typeId || t.documentType?.toLowerCase() === (row.type || '').toLowerCase());
                    }

                    if (!typeMeta) {
                      showToast('error', 'Document types not loaded yet');
                      return;
                    }

                    setEditingId(null);
                    setForm({
                      typeId: typeMeta.typeId,
                      documentType: typeMeta.documentType,
                      remarks: '',
                      file: null,
                    });
                    setIsTypeLocked(true);
                    setIsAddOpen(true);
                  }}
                >
                  <span className="material-symbols-outlined cursor-pointer icon mr-1">upload</span>
                </button>
                <button type="button" className="text-[#4D8BFF] opacity-40 cursor-not-allowed" disabled>
                  <span className="material-symbols-outlined cursor-not-allowed icon mr-1">download</span>
                </button>
                <button type="button" className="text-red-500 opacity-40 cursor-not-allowed" disabled>
                  <span className="material-symbols-outlined cursor-not-allowed icon mr-1">delete</span>
                </button>
              </>
            );
          }

          // For Uploaded rows, show Edit + Download + Delete
          if (isUploaded) {
            return (
              <>
                <button
                  type="button"
                  className="text-[#225874]"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (row?.original) {
                      openEditModal(row.original);
                    } else if (row) {
                      openEditModal(row);
                    } else {
                      showToast('error', 'Could not load document data');
                    }
                  }}
                >
                  <span className="material-symbols-outlined cursor-pointer icon mr-1">edit</span>
                </button>
                <button
                  type="button"
                  className="text-[#4D8BFF]"
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (row.fileUrl) {
                      try {
                        await downloadFile(row.fileUrl, row.type || 'document');
                      } catch (error) {
                        showToast('error', error.message || 'Failed to download file');
                      }
                    } else {
                      showToast('error', 'No file available to download.');
                    }
                  }}
                >
                  <span className="material-symbols-outlined cursor-pointer icon mr-1">download</span>
                </button>
                <button
                  type="button"
                  className="text-red-500"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(row);
                  }}
                >
                  <span className="material-symbols-outlined cursor-pointer icon mr-1">delete</span>
                </button>
              </>
            );
          }

          // Fallback: no status
          return <span className="text-xs text-gray-400">No actions</span>;
        })()}
      </div>
    );
  };

  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearch(value);
    if (props.onSearchChange) {
      props.onSearchChange(value);
    }
    if (searchTimeout) clearTimeout(searchTimeout);
    setSearchTimeout(setTimeout(() => {}, 300));
  };

  const isAddDisabled = filteredDocumentTypeOptions.length === 0;
  let submitButtonLabel = 'Upload';
  if (loading) {
    submitButtonLabel = 'Uploading...';
  } else if (editingId) {
    submitButtonLabel = 'Update';
  }

  return (
    <div className="md:col-span-3 col-span-1 flex flex-col form-section h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div className="flex items-center gap-4">
          <h2 className="font-semibold text-base sm:text-md">Documents</h2>
          <button
            type="button"
            aria-label="Add document"
            onClick={() => {
              setIsTypeLocked(false);
              setEditingId(null);
              setIsAddOpen(true);
            }}
            disabled={isAddDisabled}
            className={`text-light text-sm rounded-full w-4 h-4 items-center justify-center ${isAddDisabled ? 'bg-gray-300 cursor-not-allowed' : 'bg-orange cursor-pointer'}`}
          >
            <span className="material-symbols-outlined icon mr-1">add</span>
          </button>
        </div>

        <div className="flex flex-col sm:flex-row items-stretch sm:items-center sm:justify-end gap-2">
          <div className="relative flex items-center justify-center">
            <button type="button" className="text-text-100 absolute left-2 top-1/2 -translate-y-1/3">
              <span className="material-symbols-outlined text-border" style={{ fontVariationSettings: "'FILL' 1", fontSize: '1rem' }}>
                search
              </span>
            </button>
            <input
              type="search"
              name="search"
              placeholder="Search by type, name, or remarks..."
              value={search}
              onChange={handleSearchChange}
              className="w-full border border-border py-1.5 pl-6.5 pr-2 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-orange-500 2xl:w-[280px]"
            />
          </div>
          <button type="button" className="btn btn-primary btn-sm w-full sm:w-auto" onClick={() => setIsSamplesModalOpen(true)}>
            Samples
          </button>
        </div>
      </div>
      {/* Table */}
      <div className="flex-1 overflow-auto mt-3 sm:mt-4">
        {data && data.length > 0 ? (
          <Table columns={tableColumns} data={data} actions={tableActions} loading={loading} error={error} emptyMessage="No documents found." onRowClick={handleRowClick} />
        ) : (
          <div className="p-4 text-center text-gray-500">No documents found</div>
        )}
      </div>
      {/* Add/Edit Modal */}
      <Modal
        open={isAddOpen}
        onClose={handleCloseModal}
        title={editingId ? 'Edit Document' : 'Add Document'}
        size="sm"
        footer={
          <>
            <button type="button" className="btn btn-tertiary btn-sm" onClick={() => setForm({ typeId: '', documentType: '', remarks: '', file: null })}>
              <span className="material-symbols-outlined icon mr-1">refresh</span>
              Clear
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={() => handleSubmit(form)} disabled={!form.file || !form.typeId || loading}>
              <span className="material-symbols-outlined icon mr-1">{loading ? 'hourglass_empty' : 'upload'}</span>
              {submitButtonLabel}
            </button>
          </>
        }
      >
        <div className="grid grid-cols-1 gap-3">
          <SelectDropdown
            label="Document Type"
            options={filteredDocumentTypeOptions}
            placeholder="Select document type"
            value={form.typeId ? { value: form.typeId, label: form.documentType } : null}
            onChange={(selected) =>
              setForm((f) => ({
                ...f,
                typeId: selected?.value || '',
                documentType: selected?.label || '',
              }))
            }
            isLoading={isDocumentTypesLoading}
            // Lock document type when editing or when opened for a Pending required type
            isDisabled={isDocumentTypesLoading || isTypeLocked}
            required
          />

          {/* File Upload */}
          <div>
            <label htmlFor="file" className="block text-xs mb-2">
              Upload Documents<span className="text-red-500">*</span>
            </label>
            <div
              role="button"
              tabIndex={0}
              onClick={() => fileInputRef.current?.click()}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  fileInputRef.current?.click();
                }
              }}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
              className={`border-2 border-dashed border-gray-300 dark:border-[#f3f3f31a] hover:bg-gray-50 dark:hover:bg-[#f3f3f31a] rounded-lg p-6 flex flex-col justify-center items-center transition-colors ${
                isDragging ? 'border-orange bg-orange/5' : 'border-[#D1D5DB] dark:border-dark-border  dark:bg-card-dark'
              }`}
            >
              <span className="material-symbols-outlined text-[#9CA3AF] dark:text-dark-text-100" style={{ fontSize: '20px', color: '#4256C6' }}>
                upload
              </span>
              <span className="text-sm text-[#6B7280] dark:text-dark-text-100">{isDragging ? 'Drop file here' : 'Drop file here or click to upload.'}</span>
              <span className="text-[10px] text-[#6B7280] dark:text-dark-text-100">PDF, JPG, JPEG, PNG format upto 5 MB</span>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              className="text-xs text-[#9CA3AF] dark:text-dark-text-100 hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] || null;
                if (file) {
                  handleFileSelect(file);
                }
              }}
            />

            {form.file && (
              <div className="mt-2 flex items-center justify-between text-xs px-3 py-2 rounded-md bg-white dark:bg-dark border-gray-300 dark:border-[#f3f3f31a] hover:bg-gray-50 dark:hover:bg-[#f3f3f31a]">
                <span className="truncate">{form.file.name}</span>
                <button type="button" onClick={() => setForm((f) => ({ ...f, file: null }))} className="text-red-500 text-xs">
                  <span className="material-symbols-outlined icon mr-1">close</span>
                </button>
              </div>
            )}
          </div>

          <div>
            <label htmlFor="remarks" className="block text-xs mb-1">
              Remarks
            </label>
            <textarea
              id="remarks"
              rows={3}
              className="w-full border border-gray-300 rounded-lg p-2 text-xs focus:outline-none focus:ring-1 focus:ring-[#E47F45]"
              placeholder="Enter remarks"
              value={form.remarks}
              onChange={(e) => setForm((f) => ({ ...f, remarks: e.target.value }))}
            />
          </div>
        </div>
      </Modal>
      {/* Delete Confirmation Modal */}
      <Modal open={isDeleteOpen} size="sm" showHeader={false} showFooter={false} onClose={cancelDelete}>
        <div className="space-y-6 text-center mt-0">
          <div className="mx-auto w-12 h-12 border-[3px] border-[#e6533c] rounded-full flex items-center justify-center">
            <div className="mx-auto w-12 h-12 flex items-center justify-center">
              <span className="material-symbols-outlined text-[#e6533c]">exclamation</span>
            </div>
          </div>

          <div>
            <h3 className="text-lg font-medium">Are you Sure?</h3>
            <p className="text-xs text-gray-500 mt-1">This action is permanent and cannot be undone. Do you want to delete this document?</p>
          </div>

          <div className="flex justify-center gap-2">
            <button type="button" onClick={cancelDelete} className="btn btn-tertiary btn-sm" disabled={isUpdating}>
              Cancel
            </button>
            <button type="button" onClick={confirmDelete} className="btn btn-danger btn-sm" disabled={isUpdating}>
              {isUpdating ? (
                <>
                  <span className="material-symbols-outlined icon me-1">hourglass_empty</span>Processing...
                </>
              ) : (
                'Yes, Delete'
              )}
            </button>
          </div>
        </div>
      </Modal>
      {/* Sample Documents Modal */}
      <Modal
        open={isSamplesModalOpen}
        onClose={() => setIsSamplesModalOpen(false)}
        title="Sample Documents"
        footer={
          <>
            <button type="button" className="btn btn-tertiary btn-sm" onClick={() => setIsSamplesModalOpen(false)}>
              Cancel
            </button>
            <button type="button" className="btn btn-primary btn-sm" onClick={handleDownloadSelected} disabled={!hasSelectedItems || isDownloading}>
              {isDownloading ? (
                <>
                  <span className="material-symbols-outlined icon mr-1 animate-spin">hourglass_empty</span>
                  Downloading...
                </>
              ) : (
                'Download Selected'
              )}
            </button>
          </>
        }
      >
        {sampleDocumentsData?.data?.length ? (
          <TemplateChecklist
            items={sampleDocumentsData.data.map((doc) => ({
              id: doc.id || doc.documentId, // Ensure we have a valid ID
              label: doc.name || doc.documentName || 'Document', // Fallback label
              checked: !!checkedItems[doc.id || doc.documentId],
              onChange: () => handleCheckboxChange(doc.id || doc.documentId),
            }))}
          />
        ) : (
          <div className="p-4 text-center text-gray-500">{sampleDocumentsData?.data ? 'No sample documents available.' : 'Loading...'}</div>
        )}
      </Modal>
    </div>
  );
};

export default DocumentsList;
